/**
 * AdminPanel — M5 Admin Console.
 *
 * Three tabs: Overview (stats), Users (paginated list), Subscriptions (sub list).
 * Gated in App.js by PermissionGuard with PERM_ADMIN.
 * Uses Redux store slice `state.admin` via adminActions.
 */
import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  loadAdminStats,
  loadAdminUsers,
  changeUserRole,
  changeUserAccount,
} from '../../store/actions/adminActions';
import { logoutUser } from '../../store/actions/authActions';
import {
  SubscriptionBreakdownChart,
  HistogramChart,
  HorizontalHistogram,
  ProgressBars,
  MiniSparkline,
  StatRing,
} from './AdminOverviewCharts';
import { downloadUsersExcel } from '../../helpers/exportUsersExcel';
import './AdminPanel.css';
import InvoiceModal from '../invoice/InvoiceModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'fa-tachometer-alt' },
  { id: 'users', label: 'Users', icon: 'fa-users' },
  { id: 'subscriptions', label: 'Subscriptions', icon: 'fa-credit-card' },
];

const SIDEBAR_COLLAPSED_KEY = 'xk-admin-sidebar-collapsed';

function readSidebarCollapsed() {
  try {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (stored === null) return true;
    return stored === '1';
  } catch {
    return true;
  }
}

const ROLES_OPTIONS = ['ENGINEER', 'ADMIN'];

// Role rows are stored as ENGINEER in the database; the console labels them USER.
const ROLE_LABELS = { ENGINEER: 'USER', ADMIN: 'ADMIN' };

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// One request covers the whole table, so search and paging span every user.
const USERS_FETCH_LIMIT = 500;

const ACCOUNT_STATUS_LABELS = {
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  DEACTIVATED: 'Deactivated',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Page numbers to render for a pager, capped at seven entries.
 * `null` marks a run of pages that has been elided.
 */
function paginationRange(current, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);

  if (start > 2) pages.push(null);
  for (let p = start; p <= end; p += 1) pages.push(p);
  if (end < totalPages - 1) pages.push(null);
  pages.push(totalPages);

  return pages;
}

function subscriptionIsActive(sub) {
  if (!sub || !sub.status) return false;
  const s = String(sub.status).toUpperCase();
  const now = Date.now();
  if (s === 'ACTIVE') {
    if (!sub.endsAt) return true;
    return new Date(sub.endsAt).getTime() > now;
  }
  if (s === 'TRIAL' || s === 'TRIALING') {
    if (!sub.trialEnds) return true;
    return new Date(sub.trialEnds).getTime() > now;
  }
  return false;
}

function subscriptionAccessLabel(sub) {
  return subscriptionIsActive(sub) ? 'Active' : 'Inactive';
}

function subscriptionAccessClass(sub) {
  return subscriptionIsActive(sub) ? 'xk-admin__badge--active' : 'xk-admin__badge--expired';
}

function subscriptionExpiryDate(sub) {
  if (!sub || !sub.status) return null;
  const s = String(sub.status).toUpperCase();
  if (s === 'TRIAL' || s === 'TRIALING') return sub.trialEnds;
  return sub.endsAt;
}

function isTrialSubscription(sub) {
  if (!sub) return false;
  const s = String(sub.status || '').toUpperCase();
  return s === 'TRIAL' || s === 'TRIALING' || Boolean(sub.trialEnds);
}

function trialStartDate(sub) {
  if (!isTrialSubscription(sub)) return null;
  if (sub.createdAt) return sub.createdAt;
  if (sub.trialEnds) {
    const start = new Date(sub.trialEnds);
    start.setDate(start.getDate() - 21);
    return start;
  }
  return null;
}

function hasPaidSubscriptionEnd(sub) {
  if (!sub || !sub.endsAt) return false;
  const s = String(sub.status || '').toUpperCase();
  const plan = String(sub.planCode || '').toUpperCase();
  return s === 'ACTIVE' || (plan && plan !== 'FREE_TRIAL');
}

function shortId(value) {
  const s = String(value || '');
  if (s.length <= 16) return s;
  return `${s.slice(0, 10)}…${s.slice(-4)}`;
}

function paymentSource(sub) {
  if (!sub) return { label: '—', detail: null, kind: 'none' };
  if (sub.razorpaySubscriptionId || sub.razorpayCustomerId || sub.razorpayPlanId) {
    return {
      label: 'Razorpay',
      detail: sub.razorpaySubscriptionId
        ? `Sub ${shortId(sub.razorpaySubscriptionId)}`
        : sub.razorpayCustomerId
          ? `Cust ${shortId(sub.razorpayCustomerId)}`
          : 'Linked',
      kind: 'razorpay',
    };
  }
  const s = String(sub.status || '').toUpperCase();
  const plan = String(sub.planCode || '').toUpperCase();
  if (s === 'TRIAL' || s === 'TRIALING' || plan === 'FREE_TRIAL') {
    return { label: 'In-app trial', detail: 'No gateway payment', kind: 'trial' };
  }
  return { label: 'Manual', detail: 'Set by admin', kind: 'manual' };
}

function accountStatusClass(status) {
  const s = String(status || 'ACTIVE').toUpperCase();
  if (s === 'SUSPENDED') return 'xk-admin__badge--expired';
  if (s === 'DEACTIVATED') return 'xk-admin__badge--none';
  return 'xk-admin__badge--active';
}

function subStatusClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'ACTIVE') return 'xk-admin__badge--active';
  if (s === 'TRIAL' || s === 'TRIALING') return 'xk-admin__badge--trial';
  if (s === 'EXPIRED' || s === 'CANCELLED' || s === 'CANCELED') return 'xk-admin__badge--expired';
  return 'xk-admin__badge--none';
}

function subStatusLabel(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'TRIALING') return 'Limited Features';
  return s.charAt(0) + s.slice(1).toLowerCase() || 'None';
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const target = Number(value) || 0;
    if (target <= 0) {
      setDisplay(0);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const duration = 650;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) * (1 - t);
      setDisplay(Math.round(target * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return display;
}

AnimatedNumber.propTypes = { value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };

/** Overview tab — stats cards + interactive charts */
const OverviewTab = ({ stats, loading, error, onNavigate }) => {
  if (loading && !stats) {
    return (
      <div className="xk-admin__loading">
        <div className="xk-admin__spinner" role="status" aria-label="Loading" />
        <p>Loading statistics…</p>
      </div>
    );
  }

  const totalUsers = stats ? Number(stats.totalUsers) || 0 : 0;
  const activeSubs = stats ? Number(stats.activeSubscriptions) || 0 : 0;
  const trials = stats ? Number(stats.trials) || 0 : 0;
  const expired = stats ? Number(stats.expired) || 0 : 0;
  const verified = stats ? Number(stats.verifiedUsers) || 0 : 0;
  const noAccess = stats
    ? (stats.subscriptionBreakdown || []).find((s) => s.key === 'none')?.value || 0
    : 0;

  const signupTrend = stats?.signupsByMonth || [];
  const lastTwo = signupTrend.slice(-2);
  const signupDelta =
    lastTwo.length === 2 ? (lastTwo[1].count || 0) - (lastTwo[0].count || 0) : 0;

  const cards = [
    {
      key: 'users',
      label: 'Total users',
      value: stats ? totalUsers : 0,
      sub: stats ? `${verified} verified emails` : null,
      hint: 'Open user list',
      icon: 'fa-users',
      accent: '#1b1d21',
      tab: 'users',
      filter: null,
      progress: totalUsers ? Math.round((verified / totalUsers) * 100) : 0,
      spark: signupTrend,
      delta: signupDelta,
      deltaLabel: signupDelta >= 0 ? `+${signupDelta} this month` : `${signupDelta} this month`,
      chip: { text: `${verified} verified`, tone: 'up' },
    },
    {
      key: 'active',
      label: 'Paid users',
      value: stats ? activeSubs : 0,
      sub: stats && totalUsers ? `${Math.round((activeSubs / totalUsers) * 100)}% of users` : null,
      hint: 'View paid users',
      icon: 'fa-credit-card',
      accent: '#8CC152',
      tab: 'subscriptions',
      filter: 'ACTIVE',
      progress: totalUsers ? Math.round((activeSubs / totalUsers) * 100) : 0,
      spark: signupTrend,
      chip: { text: activeSubs ? 'Paid access' : 'No paid users', tone: activeSubs ? 'up' : 'warn' },
    },
    {
      key: 'trial',
      label: 'Free Trial with Limited Features',
      value: stats ? trials : 0,
      sub: stats && totalUsers ? `${Math.round((trials / totalUsers) * 100)}% of users` : null,
      hint: 'View trial users',
      icon: 'fa-clock',
      accent: '#9ccc65',
      tab: 'subscriptions',
      filter: 'TRIAL',
      progress: totalUsers ? Math.round((trials / totalUsers) * 100) : 0,
      spark: signupTrend,
      chip: null,
    },
    {
      key: 'expired',
      label: 'Expired',
      value: stats ? expired : 0,
      sub: stats ? `${noAccess} with no access` : null,
      hint: 'Review expired access',
      icon: 'fa-exclamation-circle',
      accent: '#DA4453',
      tab: 'subscriptions',
      filter: 'EXPIRED',
      progress: totalUsers ? Math.round(((expired + noAccess) / Math.max(totalUsers, 1)) * 100) : 0,
      spark: signupTrend,
      chip: { text: noAccess ? `${noAccess} no access` : 'All clear', tone: noAccess || expired ? 'down' : 'up' },
    },
  ];

  const subscriptionSegments = stats?.subscriptionBreakdown || [];
  const accountItems = stats?.accountBreakdown || [];
  const roleItems = stats?.roleBreakdown || [];
  const signups = stats?.signupsByMonth || [];
  const unverified = stats ? Number(stats.unverifiedUsers) || 0 : 0;

  const verificationData = stats
    ? [
        { key: 'verified', label: 'Verified', value: verified, color: '#8CC152' },
        { key: 'unverified', label: 'Unverified', value: unverified, color: '#ffb74d' },
      ]
    : [];

  const accessHistogram = subscriptionSegments.map((s) => ({
    key: s.key,
    label: s.label.replace(' plans', '').replace('Free ', ''),
    value: s.value,
    count: s.value,
    color: s.color,
  }));

  return (
    <>
      {error && <div className="xk-admin__error-banner">{error}</div>}

      <div className="xk-admin__stats xk-admin__stats--enhanced">
        {cards.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`xk-admin__stat-card xk-admin__stat-card--enhanced xk-admin__stat-card--${c.key}`}
            style={{ '--stat-accent': c.accent }}
            onClick={() => onNavigate && onNavigate(c.tab, c.filter)}
            aria-label={`${c.label}: ${c.value}. ${c.hint}`}
          >
            <div className="xk-admin__stat-card-top">
              <StatRing percent={c.progress} color={c.accent}>
                <span
                  className="xk-admin__stat-icon"
                  style={{ background: `${c.accent}18`, color: c.accent }}
                >
                  <i className={`fa ${c.icon}`} />
                </span>
              </StatRing>
              <div className="xk-admin__stat-heading">
                <div className="xk-admin__stat-label">{c.label}</div>
                {c.deltaLabel && (
                  <span
                    className={`xk-admin__stat-delta ${
                      c.delta >= 0 ? 'xk-admin__stat-delta--up' : 'xk-admin__stat-delta--down'
                    }`}
                  >
                    {c.deltaLabel}
                  </span>
                )}
                {c.chip && (
                  <span className={`xk-admin__stat-chip xk-admin__stat-chip--${c.chip.tone}`}>
                    {c.chip.text}
                  </span>
                )}
              </div>
              {stats && c.spark && c.spark.length > 0 && (
                <MiniSparkline points={c.spark} color={c.accent} />
              )}
            </div>
            <div className="xk-admin__stat-value-row">
              <div className="xk-admin__stat-value">
                {stats ? <AnimatedNumber value={c.value} /> : '—'}
              </div>
              <div className="xk-admin__stat-pct">{c.progress}%</div>
            </div>
            {c.sub && <div className="xk-admin__stat-sub">{c.sub}</div>}
            <div className="xk-admin__stat-meter" aria-hidden="true">
              <span style={{ width: `${Math.min(100, c.progress)}%` }} />
            </div>
            <span className="xk-admin__stat-cta">
              {c.hint} <i className="fa fa-arrow-right" />
            </span>
          </button>
        ))}
      </div>

      {stats ? (
        <div className="xk-admin__overview">
          <div className="xk-admin__card xk-admin__chart-card xk-admin__chart-card--featured">
            <SubscriptionBreakdownChart
              segments={subscriptionSegments}
              totalUsers={totalUsers}
            />
          </div>

          <div className="xk-admin__charts-grid xk-admin__charts-grid--3">
            <div className="xk-admin__card xk-admin__chart-card">
              <HistogramChart
                data={signups}
                title="Sign-up histogram"
                subtitle="New registrations per month"
                color="#8CC152"
                accentColor="#DA4453"
              />
            </div>

            <div className="xk-admin__card xk-admin__chart-card">
              <HistogramChart
                data={verificationData}
                title="Email verification"
                subtitle="Verified vs pending users"
                color="#9ccc65"
                accentColor="#1b1d21"
              />
            </div>

            <div className="xk-admin__card xk-admin__chart-card">
              <HorizontalHistogram
                items={accessHistogram}
                title="Access levels"
                subtitle="Users by subscription tier"
              />
            </div>
          </div>

          <div className="xk-admin__charts-grid">
            <div className="xk-admin__card xk-admin__chart-card">
              <ProgressBars
                title="Account status"
                subtitle="Active, suspended and deactivated"
                items={accountItems}
              />
            </div>


          </div>
        </div>
      ) : (
        <div className="xk-admin__card">
          <div className="xk-admin__empty">No data loaded yet.</div>
        </div>
      )}
    </>
  );
};

OverviewTab.propTypes = {
  stats: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onNavigate: PropTypes.func,
};

OverviewTab.defaultProps = { stats: null, loading: false, error: null, onNavigate: null };

/** Account detail modal — suspend / deactivate / reactivate */
const UserAccountModal = ({ user, busy, onClose, onAccountChange }) => {
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  if (!user) return null;

  const accountStatus = String(user.accountStatus || 'ACTIVE').toUpperCase();
  const sub = user.subscription;
  const isActive = accountStatus === 'ACTIVE';
  const pendingLabel = pending === 'SUSPENDED' ? 'suspend' : 'deactivate';

  const startRestrict = (nextStatus) => {
    setPending(nextStatus);
    setReason('');
    setReasonError('');
  };

  const confirmRestrict = () => {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setReasonError('Please enter a reason (at least 3 characters).');
      return;
    }
    onAccountChange(user.id, pending, trimmed);
  };

  return (
    <div className="xk-admin__modal-overlay" onClick={onClose} role="presentation">
      <div
        className="xk-admin__modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="xk-admin-account-title"
      >
        <div className="xk-admin__modal-head">
          <h2 id="xk-admin-account-title" className="xk-admin__modal-title">
            Account details
          </h2>
          <button type="button" className="xk-admin__modal-close" onClick={onClose} aria-label="Close">
            <i className="fa fa-times" />
          </button>
        </div>

        <div className="xk-admin__modal-body">
          <div className="xk-admin__modal-user">
            <div className="xk-admin__cell-name">{user.name || user.email}</div>
            <div className="xk-admin__cell-email">{user.email}</div>
          </div>

          <dl className="xk-admin__modal-meta">
            <div>
              <dt>Account status</dt>
              <dd>
                <span className={`xk-admin__badge ${accountStatusClass(accountStatus)}`}>
                  {ACCOUNT_STATUS_LABELS[accountStatus] || accountStatus}
                </span>
              </dd>
            </div>
            <div>
              <dt>Subscription</dt>
              <dd>
                <span className={`xk-admin__badge ${subscriptionAccessClass(sub)}`}>
                  {subscriptionAccessLabel(sub)}
                </span>
              </dd>
            </div>
            <div>
              <dt>Joined</dt>
              <dd>{formatDate(user.createdAt)}</dd>
            </div>
            <div>
              <dt>Expires</dt>
              <dd>{formatDate(subscriptionExpiryDate(sub))}</dd>
            </div>
          </dl>

          {!isActive && user.accountStatusReason && (
            <div className="xk-admin__reason-note">
              <div className="xk-admin__reason-note-label">Reason</div>
              <p>{user.accountStatusReason}</p>
            </div>
          )}

          {pending ? (
            <div className="xk-admin__reason-form">
              <label htmlFor="xk-admin-reason">
                Why {pendingLabel} this account?
              </label>
              <textarea
                id="xk-admin-reason"
                className="xk-admin__reason-input"
                rows={3}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (reasonError) setReasonError('');
                }}
                placeholder="Enter a reason (required)"
                disabled={busy}
              />
              {reasonError && <div className="xk-admin__reason-error">{reasonError}</div>}
              <div className="xk-admin__modal-actions">
                <button
                  type="button"
                  className="xk-admin__btn"
                  disabled={busy}
                  onClick={() => {
                    setPending(null);
                    setReason('');
                    setReasonError('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`xk-admin__btn ${
                    pending === 'SUSPENDED' ? 'xk-admin__btn--warn' : 'xk-admin__btn--danger'
                  }`}
                  disabled={busy}
                  onClick={confirmRestrict}
                >
                  {busy ? '…' : `Confirm ${pendingLabel}`}
                </button>
              </div>
            </div>
          ) : (
            <div className="xk-admin__modal-actions">
              {isActive ? (
                <>
                  <button
                    type="button"
                    className="xk-admin__btn xk-admin__btn--warn"
                    disabled={busy}
                    onClick={() => startRestrict('SUSPENDED')}
                  >
                    Suspend account
                  </button>
                  <button
                    type="button"
                    className="xk-admin__btn xk-admin__btn--danger"
                    disabled={busy}
                    onClick={() => startRestrict('DEACTIVATED')}
                  >
                    Deactivate account
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="xk-admin__btn xk-admin__btn--primary"
                  disabled={busy}
                  onClick={() => onAccountChange(user.id, 'ACTIVE')}
                >
                  Reactivate account
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

UserAccountModal.propTypes = {
  user: PropTypes.object,
  busy: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAccountChange: PropTypes.func.isRequired,
};

UserAccountModal.defaultProps = { user: null, busy: false };

/** Users tab: searchable, paginated table */
const UsersTab = ({ users, total, loading, error, onRoleChange, onAccountChange, busy }) => {
  const [search, setSearch] = useState('');
  const [roleTarget, setRoleTarget] = useState({});
  const [accountUser, setAccountUser] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.name || '').toLowerCase().includes(q) ||
        (u.organization || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * pageSize;
  const pageRows = useMemo(
    () => filtered.slice(firstIndex, firstIndex + pageSize),
    [filtered, firstIndex, pageSize]
  );

  // A narrower search or a smaller page size can strand the reader past the last page.
  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  const handleExportExcel = () => {
    if (!filtered.length) return;
    downloadUsersExcel(filtered).catch((e) => console.error('Export failed', e)); // eslint-disable-line no-console
  };

  return (
    <>
      {error && <div className="xk-admin__error-banner">{error}</div>}

      {accountUser && (
        <UserAccountModal
          user={users.find((u) => u.id === accountUser.id) || accountUser}
          busy={Boolean(busy[accountUser.id])}
          onClose={() => setAccountUser(null)}
          onAccountChange={async (userId, status, reason) => {
            const ok = await onAccountChange(userId, status, reason);
            if (ok) setAccountUser(null);
          }}
        />
      )}

      <div className="xk-admin__card">
        <div className="xk-admin__card-toolbar">
          <input
            type="text"
            className="xk-admin__search"
            placeholder="Search by name, email or organisation…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="xk-admin__toolbar-actions">
            <span className="xk-admin__meta">
              {filtered.length} of {total} user{total !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              className="xk-admin__btn xk-admin__btn--export"
              disabled={!filtered.length || loading}
              onClick={handleExportExcel}
              title="Export users to Excel"
              aria-label="Export users to Excel"
            >
              <svg
                className="xk-admin__export-icon"
                viewBox="0 0 24 24"
                width="22"
                height="22"
                aria-hidden="true"
                focusable="false"
              >
                {/* Document with folded corner */}
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                  d="M7 3.5h6.5L17.5 7.5V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z"
                />
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                  d="M13.5 3.5V7.5H17.5"
                />
                {/* Outward export arrow */}
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 13.5h6.5M13 10.5l3.5 3-3.5 3"
                />
              </svg>
            </button>
          </div>
        </div>

        {loading && users.length === 0 ? (
          <div className="xk-admin__loading">
            <div className="xk-admin__spinner" role="status" aria-label="Loading" />
            <p>Loading users…</p>
          </div>
        ) : (
          <>
            <div className="xk-admin__table-wrap">
              <table className="xk-admin__table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Organisation</th>
                    <th>Role</th>
                    <th>Subscription</th>
                    <th>Dates</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="xk-admin__empty">No users match your search.</div>
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((u) => {
                      const currentRole = roleTarget[u.id] || u.role || 'ENGINEER';
                      const roleChanged = currentRole !== (u.role || 'ENGINEER');
                      const sub = u.subscription;
                      const acctStatus = String(u.accountStatus || 'ACTIVE').toUpperCase();
                      const subLabel =
                        acctStatus !== 'ACTIVE'
                          ? 'Inactive'
                          : subscriptionAccessLabel(sub);

                      return (
                        <tr key={u.id}>
                          {/* User info */}
                          <td>
                            <div className="xk-admin__cell-name">
                              {u.name || <span style={{ color: '#bbb' }}>—</span>}
                            </div>
                            <div className="xk-admin__cell-email">{u.email}</div>
                            {!u.emailVerifiedAt && (
                              <div className="xk-admin__cell-warn">Email not verified</div>
                            )}
                          </td>

                          <td>
                            <div className="xk-admin__cell-muted">
                              {u.organization || <span style={{ color: '#bbb' }}>—</span>}
                            </div>
                            {u.country && <div className="xk-admin__cell-sub">{u.country}</div>}
                          </td>

                          {/* Role */}
                          <td className="xk-admin__cell-role">
                            <select
                              className="xk-admin__select xk-admin__select--role"
                              value={currentRole}
                              onChange={(e) =>
                                setRoleTarget((prev) => ({ ...prev, [u.id]: e.target.value }))
                              }
                              disabled={Boolean(busy[u.id])}
                            >
                              {ROLES_OPTIONS.map((r) => (
                                <option key={r} value={r}>
                                  {ROLE_LABELS[r] || r}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* Subscription — read-only Active / Inactive */}
                          <td>
                            <span
                              className={`xk-admin__badge ${
                                subLabel === 'Active'
                                  ? 'xk-admin__badge--active'
                                  : 'xk-admin__badge--expired'
                              }`}
                            >
                              {subLabel}
                            </span>
                            {acctStatus !== 'ACTIVE' && (
                              <div className="xk-admin__cell-sub">
                                {ACCOUNT_STATUS_LABELS[acctStatus]}
                              </div>
                            )}
                          </td>

                          <td className="xk-admin__cell-date">
                            <div>Joined {formatDate(u.createdAt)}</div>
                            <div className="xk-admin__cell-sub">
                              Expires {formatDate(subscriptionExpiryDate(sub))}
                            </div>
                          </td>

                          <td>
                            <div className="xk-admin__cell-actions">
                              <button
                                type="button"
                                className="xk-admin__btn xk-admin__btn--icon"
                                title="View account"
                                aria-label="View account"
                                disabled={Boolean(busy[u.id])}
                                onClick={() => setAccountUser(u)}
                              >
                                <i className="fa fa-eye" />
                              </button>
                              {roleChanged && (
                                <button
                                  type="button"
                                  className="xk-admin__btn xk-admin__btn--primary"
                                  disabled={Boolean(busy[u.id])}
                                  onClick={() => {
                                    onRoleChange(u.id, currentRole);
                                    setRoleTarget((prev) => {
                                      const n = { ...prev };
                                      delete n[u.id];
                                      return n;
                                    });
                                  }}
                                >
                                  {busy[u.id] ? '…' : 'Save role'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filtered.length > 0 && (
              <div className="xk-admin__pagination">
                <span className="xk-admin__pagination-info">
                  Showing {firstIndex + 1}–{Math.min(firstIndex + pageSize, filtered.length)} of{' '}
                  {filtered.length}
                </span>

                <label className="xk-admin__pagination-size">
                  <span>Rows per page</span>
                  <select
                    className="xk-admin__select xk-admin__select--page-size"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    aria-label="Rows per page"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="xk-admin__pagination-pages">
                  <button
                    type="button"
                    className="xk-admin__btn xk-admin__btn--page"
                    disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                    aria-label="Previous page"
                  >
                    <i className="fa fa-chevron-left" />
                  </button>

                  {paginationRange(currentPage, totalPages).map((p, i) =>
                    p === null ? (
                      <span key={`gap-${i}`} className="xk-admin__pagination-gap">
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        className={`xk-admin__btn xk-admin__btn--page ${
                          p === currentPage ? 'xk-admin__btn--active' : ''
                        }`}
                        onClick={() => setPage(p)}
                        aria-current={p === currentPage ? 'page' : undefined}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button
                    type="button"
                    className="xk-admin__btn xk-admin__btn--page"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage(currentPage + 1)}
                    aria-label="Next page"
                  >
                    <i className="fa fa-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

UsersTab.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object),
  total: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRoleChange: PropTypes.func.isRequired,
  onAccountChange: PropTypes.func.isRequired,
  busy: PropTypes.object,
};

UsersTab.defaultProps = {
  users: [],
  total: 0,
  loading: false,
  error: null,
  busy: {},
};

const SubscriptionsTab = ({ users, loading, error, initialFilter }) => {
  const [filter, setFilter] = useState(initialFilter || 'ALL');
  // invoiceUser: the user whose invoice modal is open (null = closed)
  const [invoiceUser, setInvoiceUser] = useState(null);

  useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);

  const subsUsers = useMemo(
    () => users.filter((u) => u.subscription && u.subscription.status),
    [users]
  );

  const displayed = useMemo(() => {
    if (filter === 'ALL') return subsUsers;
    return subsUsers.filter((u) => {
      const s = String((u.subscription && u.subscription.status) || '').toUpperCase();
      if (filter === 'TRIAL') return s === 'TRIAL' || s === 'TRIALING';
      return s === filter.toUpperCase();
    });
  }, [subsUsers, filter]);

  const filterBtns = ['ALL', 'ACTIVE', 'TRIAL', 'EXPIRED', 'NONE'];

  return (
    <>
      {error && <div className="xk-admin__error-banner">{error}</div>}

      {loading && subsUsers.length === 0 ? (
        <div className="xk-admin__loading">
          <div className="xk-admin__spinner" role="status" aria-label="Loading" />
          <p>Loading subscriptions…</p>
        </div>
      ) : (
        <div className="xk-admin__card">
          <div className="xk-admin__card-toolbar">
            <div className="xk-admin__filters">
              {filterBtns.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`xk-admin__btn ${filter === f ? 'xk-admin__btn--active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'ALL' ? 'All' : subStatusLabel(f)}
                </button>
              ))}
            </div>
            <span className="xk-admin__meta">
              {displayed.length} record{displayed.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="xk-admin__table-wrap">
            <table className="xk-admin__table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Trial</th>
                  <th>Subscription Ends</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="xk-admin__empty">
                        No subscriptions match the selected filter.
                      </div>
                    </td>
                  </tr>
                ) : (
                    displayed.map((u) => {
                    const sub = u.subscription;
                    const statusKey = String(
                      (sub && sub.status) || 'NONE'
                    ).toUpperCase();
                    const pay = paymentSource(sub);
                    return (
                      <tr key={u.id}>
                        <td>
                          <div className="xk-admin__cell-name">{u.name || u.email}</div>
                          <div className="xk-admin__cell-email">{u.email}</div>
                        </td>

                        <td>
                          <span
                            className={`xk-admin__badge ${subStatusClass(statusKey)}`}
                          >
                            {subStatusLabel(statusKey)}
                          </span>
                        </td>
                        <td className="xk-admin__cell-muted">
                          {(sub && sub.planCode) || <span style={{ color: '#bbb' }}>—</span>}
                        </td>
                        <td className="xk-admin__cell-date">
                          {isTrialSubscription(sub) ? (
                            <>
                              <div>Start {formatDate(trialStartDate(sub))}</div>
                              <div className="xk-admin__cell-sub">
                                End {formatDate(sub && sub.trialEnds)}
                              </div>
                            </>
                          ) : (
                            <span style={{ color: '#bbb' }}>—</span>
                          )}
                        </td>
                        <td className="xk-admin__cell-date">
                          {hasPaidSubscriptionEnd(sub)
                            ? formatDate(sub.endsAt)
                            : <span style={{ color: '#bbb' }}>—</span>}
                        </td>
                        {/* ── Invoice action 'i' button ── */}
                        <td style={{ verticalAlign: 'middle', padding: '10px 14px' }}>
                          <button
                            type="button"
                            title="Invoices & Info"
                            aria-label="Invoices & Info"
                            onClick={() => setInvoiceUser(u)}
                            className="xk-admin__action-btn-info"
                          >
                            <i className="fa fa-info" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Manager Modal — shown when the 🔵 button is clicked */}
      {invoiceUser && (
        <InvoiceModal
          user={invoiceUser}
          onClose={() => setInvoiceUser(null)}
        />
      )}
    </>
  );
};

SubscriptionsTab.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  error: PropTypes.string,
  initialFilter: PropTypes.string,
};

SubscriptionsTab.defaultProps = { users: [], loading: false, error: null, initialFilter: 'ALL' };

// ─── Main AdminPanel component ────────────────────────────────────────────────

const AdminPanel = ({
  stats,
  users,
  total,
  loading,
  error,
  loadAdminStatsAction,
  loadAdminUsersAction,
  changeUserRoleAction,
  changeUserAccountAction,
  logoutUserAction,
}) => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptionFilter, setSubscriptionFilter] = useState('ALL');
  const [busy, setBusy] = useState({});
  const [signingOut, setSigningOut] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const navRef = useRef(null);
  const navItemRefs = useRef({});
  const [navIndicator, setNavIndicator] = useState({ top: 0, height: 0, ready: false });

  useLayoutEffect(() => {
    const updateIndicator = () => {
      const nav = navRef.current;
      const activeEl = navItemRefs.current[activeTab];
      if (!nav || !activeEl) return;
      const navRect = nav.getBoundingClientRect();
      const itemRect = activeEl.getBoundingClientRect();
      setNavIndicator({
        top: itemRect.top - navRect.top,
        height: itemRect.height,
        ready: true,
      });
    };

    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeTab, sidebarCollapsed]);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  // Load data when tabs are activated
  useEffect(() => {
    if (activeTab === 'overview') {
      loadAdminStatsAction().catch(() => {});
    }
    if (activeTab === 'users' || activeTab === 'subscriptions') {
      loadAdminUsersAction(1, USERS_FETCH_LIMIT).catch(() => {});
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigate = (tab, filter) => {
    if (tab === 'subscriptions') {
      setSubscriptionFilter(filter || 'ALL');
    }
    setActiveTab(tab);
  };

  const handleRoleChange = async (userId, role) => {
    setBusy((prev) => ({ ...prev, [userId]: true }));
    try {
      await changeUserRoleAction(userId, role);
    } catch {
      // error surfaces via Redux state.admin.error
    } finally {
      setBusy((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleAccountChange = async (userId, accountStatus, reason) => {
    setBusy((prev) => ({ ...prev, [userId]: true }));
    try {
      await changeUserAccountAction(userId, accountStatus, reason);
      return true;
    } catch {
      return false;
    } finally {
      setBusy((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleLogout = async () => {
    setSigningOut(true);
    try {
      await logoutUserAction();
    } finally {
      setSigningOut(false);
      history.push('/login');
    }
  };

  const pageTitles = {
    overview: { title: 'Overview', sub: 'Platform health at a glance' },
    users: { title: 'User Management', sub: 'Browse, search and manage registered users' },
    subscriptions: { title: 'Subscriptions', sub: 'Monitor and filter subscription statuses' },
  };

  const current = pageTitles[activeTab] || pageTitles.overview;

  return (
    <div className={`xk-admin ${sidebarCollapsed ? 'xk-admin--sidebar-collapsed' : ''}`}>
      <aside className={`xk-admin__sidebar ${sidebarCollapsed ? 'xk-admin__sidebar--collapsed' : ''}`}>
        <div className="xk-admin__sidebar-head">
          <img
            src={`${process.env.PUBLIC_URL || ''}/XLog3D Logo_Red.png`}
            alt="XLog3D"
            className="xk-admin__sidebar-logo"
          />
        </div>

        <nav className="xk-admin__nav" ref={navRef}>
          <span
            className={`xk-admin__nav-slider ${navIndicator.ready ? 'xk-admin__nav-slider--ready' : ''}`}
            style={{
              transform: `translateY(${navIndicator.top}px)`,
              height: `${navIndicator.height}px`,
            }}
            aria-hidden="true"
          />
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              ref={(el) => {
                navItemRefs.current[tab.id] = el;
              }}
              className={`xk-admin__nav-item ${activeTab === tab.id ? 'xk-admin__nav-item--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-tooltip={tab.label}
              aria-label={tab.label}
            >
              <span className="xk-admin__nav-icon" aria-hidden="true">
                <i className={`fa ${tab.icon}`} />
              </span>
              <span className="xk-admin__nav-label">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="xk-admin__sidebar-foot">
          <button
            type="button"
            className="xk-admin__back-btn"
            onClick={() => history.push('/')}
            data-tooltip="Back to app"
            aria-label="Back to app"
          >
            <i className="fa fa-arrow-left" aria-hidden="true" />
            <span className="xk-admin__sidebar-btn-label">Back to app</span>
          </button>
          <button
            type="button"
            className="xk-admin__logout-btn"
            onClick={handleLogout}
            disabled={signingOut}
            data-tooltip={signingOut ? 'Signing out…' : 'Log out'}
            aria-label="Log out"
          >
            <i className="fa fa-sign-out-alt" aria-hidden="true" />
            <span className="xk-admin__sidebar-btn-label">
              {signingOut ? 'Signing out…' : 'Log out'}
            </span>
          </button>
        </div>

        <button
          type="button"
          className="xk-admin__sidebar-toggle"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!sidebarCollapsed}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <i className={`fa fa-chevron-${sidebarCollapsed ? 'right' : 'left'}`} aria-hidden="true" />
        </button>
      </aside>

      <main className="xk-admin__main">
        <div key={activeTab} className="xk-admin__main-content">
          <div className="xk-admin__page-head">
            <h1 className="xk-admin__page-title">{current.title}</h1>
            <p className="xk-admin__page-sub">{current.sub}</p>
          </div>

          {activeTab === 'overview' && (
            <OverviewTab
              stats={stats}
              loading={loading}
              error={error}
              onNavigate={handleNavigate}
            />
          )}

          {activeTab === 'users' && (
            <UsersTab
              users={users}
              total={total}
              loading={loading}
              error={error}
              onRoleChange={handleRoleChange}
              onAccountChange={handleAccountChange}
              busy={busy}
            />
          )}

          {activeTab === 'subscriptions' && (
            <SubscriptionsTab
              users={users}
              loading={loading}
              error={error}
              initialFilter={subscriptionFilter}
            />
          )}
        </div>
      </main>
    </div>
  );
};

AdminPanel.propTypes = {
  stats: PropTypes.object,
  users: PropTypes.arrayOf(PropTypes.object),
  total: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
  loadAdminStatsAction: PropTypes.func.isRequired,
  loadAdminUsersAction: PropTypes.func.isRequired,
  changeUserRoleAction: PropTypes.func.isRequired,
  changeUserAccountAction: PropTypes.func.isRequired,
  logoutUserAction: PropTypes.func.isRequired,
};

AdminPanel.defaultProps = {
  stats: null,
  users: [],
  total: 0,
  loading: false,
  error: null,
};

const mapStateToProps = (state) => ({
  stats: state.admin.stats,
  users: state.admin.users,
  total: state.admin.total,
  loading: state.admin.loading,
  error: state.admin.error,
});

const mapDispatchToProps = {
  loadAdminStatsAction: loadAdminStats,
  loadAdminUsersAction: loadAdminUsers,
  changeUserRoleAction: changeUserRole,
  changeUserAccountAction: changeUserAccount,
  logoutUserAction: logoutUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminPanel);
